import type { UserRole } from '@doneverse/shared'
import { useNavigate } from '@tanstack/react-router'
import { MessageSquarePlus } from 'lucide-react'
import { forwardRef, useState } from 'react'
import { FeedbackFormDialog } from './FeedbackFormDialog'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/contexts/AuthContext'
import { FEEDBACK_SURFACE_ROLES } from '@/lib/feedback-access'
import { cn } from '@/lib/utils'

/**
 * Where "View" sends this submitter. The button is on every page for every
 * role, but the two surfaces are gated differently: admins track items in the
 * queue, everyone else reads the thread at /feedback. A role we can't place
 * (the profile hasn't loaded) gets no link rather than a redirect that dumps
 * them on their home page.
 */
function viewRouteFor(role: UserRole | undefined, feedbackId: string) {
  if (role === 'admin') {
    return { to: '/admin/feedback/$feedbackId' as const, params: { feedbackId } }
  }
  if (role && FEEDBACK_SURFACE_ROLES.includes(role)) {
    return { to: '/feedback/$feedbackId' as const, params: { feedbackId } }
  }
  return null
}

/**
 * Global entry point to the Feedback Loop, on every page via TopBarActions.
 *
 * The confirmation is the point: the single biggest gap in the old experience
 * was that a founder could not tell whether their feedback reached anyone. The
 * toast says it landed and offers the thread it landed in.
 *
 * This also owns the minimised state, because the launcher is where a draft has
 * to come back from. The dot is the whole affordance: a founder who minimised
 * the form to look at the page behind it needs to see, from the page, that the
 * report is still there.
 */
export const FeedbackButton = forwardRef<HTMLButtonElement, { className?: string }>(
  ({ className, ...props }, ref) => {
    const [open, setOpen] = useState(false)
    const [hasDraft, setHasDraft] = useState(false)
    const navigate = useNavigate()
    const { profile } = useAuth()

    // Only while the form is out of sight — an open dialog is its own evidence.
    const showDraft = hasDraft && !open

    return (
      <>
        <Button
          ref={ref}
          {...props}
          className={cn('h-7 rounded-full px-4 text-xs', className)}
          onClick={() => setOpen(true)}
          aria-label={showDraft ? 'Resume feedback draft' : 'Send feedback'}
          title={showDraft ? 'Resume your feedback draft' : undefined}
        >
          <MessageSquarePlus />
          Feedback
          {showDraft && (
            <span
              aria-hidden="true"
              data-testid="feedback-draft-dot"
              className="ml-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current"
            />
          )}
        </Button>

        <FeedbackFormDialog
          open={open}
          onOpenChange={setOpen}
          onDraftChange={setHasDraft}
          onSubmitted={(feedbackId) => {
            const route = viewRouteFor(profile?.role, feedbackId)
            toast.success("Thanks — we've got this.", {
              description: "You'll get a notification when there's an update.",
              action: route ? { label: 'View', onClick: () => navigate(route) } : undefined,
            })
          }}
        />
      </>
    )
  },
)
FeedbackButton.displayName = 'FeedbackButton'
